// The relay button every Flutter touch screen draws (fourTouchScreen.dart
// buildRaisedDeviceButton, and the identical copies in oneTouch / twoTouch):
//
//   • a ButtonColor → FontColor linear gradient whose DIRECTION flips with state
//     (bottom→top when on, top→bottom when off)
//   • 20px radius, and a shadow that changes with state: a soft FontColor glow
//     when on, a small black drop shadow when off
//   • the output name in bold black over a power icon
//   • the power icon is GREEN #66FF00 on, RED off, BLUE when the state is
//     unknown — see useRelayStatus for why unknown is its own colour
//   • long-press opens the Schedule screen for the output
//
// ── Button-settings marks ──────────────────────────────────────────────────
// Corner badges saying what is CONFIGURED on this button (see
// core/devices/buttonStatus.js). Icons, not sentences — the tile is small:
//
//   top-left      clock  + time   a schedule fires later today ("3:50 pm")
//                 clock alone     a schedule exists, but nothing left today
//   bottom-left   timer  + "3m"   switches itself off N minutes after a press
//   top-right     router          paired with another switch (two-way)

import React from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import RouterIcon from '@mui/icons-material/Router';
import useLongPress from '../../../components/useLongPress';

// A corner badge: icon, optional short label, on a dark pill so it stays legible
// over the tile's gradient (which runs light at one end whatever the theme).
function Mark({ icon, label, sx }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: label ? 0.5 : 0.25,
        py: 0.125,
        borderRadius: '8px',
        bgcolor: 'rgba(0,0,0,0.55)',
        color: '#fff',
        pointerEvents: 'none',
        ...sx,
      }}
    >
      {icon}
      {label && (
        <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

export const ON_GREEN = '#66FF00';

export function relayColor(isOn, isKnown) {
  if (!isKnown) return '#2196F3';
  return isOn ? ON_GREEN : '#F44336';
}

export default function RelayTile({
  name,
  isOn,
  isKnown,
  scheduled,
  marks,          // from buttonStatus.marksFor() — optional
  onToggle,
  onSchedule,
  width,
  height,
  iconSize,
  nameFont,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const press = useLongPress(onSchedule, onToggle);

  return (
    <Box sx={{ position: 'relative', width, height }}>
      <ButtonBase
        {...press}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '20px',
          background: isOn
            ? `linear-gradient(to top, ${c.button}, ${c.text})`
            : `linear-gradient(to bottom, ${c.button}, ${c.text})`,
          boxShadow: isOn
            ? `0 0 18px 4px ${c.text}33`
            : '0 3px 6px rgba(0,0,0,0.2)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.75 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: nameFont,
              color: '#000',
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {name}
          </Typography>
          <Box sx={{ height: 8 }} />
          <PowerSettingsNewIcon sx={{ fontSize: iconSize, color: relayColor(isOn, isKnown) }} />
        </Box>
      </ButtonBase>

      {/* Schedule. With a pending time today it shows the time; otherwise the
          bare clock, which is the old badge's meaning ("this has a schedule"). */}
      {(marks?.pending || scheduled || marks?.hasSchedule) && (
        <Mark
          sx={{ top: 6, left: 6 }}
          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
          label={marks?.pendingLabel || ''}
        />
      )}

      {/* Auto-off delay — "off 3m after a press". */}
      {marks?.autoOff > 0 && (
        <Mark
          sx={{ bottom: 6, left: 6 }}
          icon={<TimerOutlinedIcon sx={{ fontSize: 14 }} />}
          label={marks.autoOffLabel}
        />
      )}

      {/* Two-way ("router") — this button is paired with another switch. */}
      {marks?.partner && (
        <Mark
          sx={{ top: 6, right: 6 }}
          icon={<RouterIcon sx={{ fontSize: 14 }} />}
        />
      )}
    </Box>
  );
}
